/**
 * @file
 * @fileOverview A-Frame component for creating a cinematic camera effect with letterboxing and vignette.
 * @version 1.0.0
 * @author Parker Eli Bidigare
 * @date 2024-05-26
 */

import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { TAARenderPass } from 'three/examples/jsm/postprocessing/TAARenderPass.js';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';
import { BokehPass } from 'three/examples/jsm/postprocessing/BokehPass.js';
import { FXAAShader } from 'three/examples/jsm/shaders/FXAAShader.js';
import { SMAAPass } from 'three/examples/jsm/postprocessing/SMAAPass.js';
import { SSAOPass } from 'three/examples/jsm/postprocessing/SSAOPass.js';
import { CopyShader } from 'three/examples/jsm/shaders/CopyShader.js';

AFRAME.registerComponent('cinematic-camera', {
    schema: {
      letterboxHeight: {type: 'number', default: 0.1},
      vignetteStrength: {type: 'number', default: 0.5},
      vignetteColor: {type: 'color', default: '#000000'},
      transitionDuration: {type: 'number', default: 1000},
      active: {type: 'boolean', default: true}
    },
  
    init: function() {
      this.camera = this.el.getObject3D('camera');
      this.scene = this.el.sceneEl;
      this.letterboxTop = null;
      this.letterboxBottom = null;
      this.vignette = null;
      this.createLetterbox();
      this.createVignette();
      this.updateEffect(this.data.active);
    },
  
    update: function(oldData) {
      if (this.data.active !== oldData.active) {
        this.updateEffect(this.data.active);
      }
      if (this.data.letterboxHeight !== oldData.letterboxHeight) {
        this.updateLetterbox();
      }
      if (this.data.vignetteStrength !== oldData.vignetteStrength || 
          this.data.vignetteColor !== oldData.vignetteColor) {
        this.updateVignette();
      }
    },
  
    createLetterbox: function() {
      this.letterboxTop = document.createElement('a-plane');
      this.letterboxBottom = document.createElement('a-plane');
  
      const commonAttributes = {
        color: '#000',
        opacity: 1,
        position: '0 0 -0.1',
        rotation: '0 0 0',
        material: 'shader: flat',
        width: 2
      };
  
      Object.assign(this.letterboxTop.setAttribute('geometry', {height: this.data.letterboxHeight}));
      Object.assign(this.letterboxBottom.setAttribute('geometry', {height: this.data.letterboxHeight}));
  
      this.updateLetterbox();
  
      this.el.appendChild(this.letterboxTop);
      this.el.appendChild(this.letterboxBottom);
    },
  
    updateLetterbox: function() {
      this.letterboxTop.setAttribute('position', `0 ${0.5 + this.data.letterboxHeight / 2} -0.1`);
      this.letterboxBottom.setAttribute('position', `0 ${-0.5 - this.data.letterboxHeight / 2} -0.1`);
      this.letterboxTop.setAttribute('geometry', 'height', this.data.letterboxHeight);
      this.letterboxBottom.setAttribute('geometry', 'height', this.data.letterboxHeight);
    },
  
    createVignette: function() {
      this.vignette = document.createElement('a-entity');
      this.vignette.setAttribute('geometry', {
        primitive: 'plane',
        width: 2,
        height: 2
      });
      this.updateVignette();
      this.vignette.setAttribute('position', '0 0 -0.05');
      this.el.appendChild(this.vignette);
    },
  
    updateVignette: function() {
      this.vignette.setAttribute('material', {
        shader: 'flat',
        src: this.createVignetteTexture(),
        transparent: true,
        opacity: this.data.vignetteStrength
      });
    },
  
    createVignetteTexture: function() {
      const canvas = document.createElement('canvas');
      canvas.width = 256;
      canvas.height = 256;
      const ctx = canvas.getContext('2d');
      
      const gradient = ctx.createRadialGradient(128, 128, 0, 128, 128, 128);
      gradient.addColorStop(0, 'rgba(0,0,0,0)');
      gradient.addColorStop(1, this.data.vignetteColor);
      
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, 256, 256);
      
      return canvas.toDataURL();
    },
  
    updateEffect: function(active) {
      const elements = [this.letterboxTop, this.letterboxBottom, this.vignette];
      const targetOpacity = active ? 1 : 0;
      
      elements.forEach(el => {
        el.setAttribute('animation__opacity', {
          property: 'material.opacity',
          to: targetOpacity,
          dur: this.data.transitionDuration,
          easing: 'easeInOutQuad'
        });
      });
    },
  
    remove: function() {
      if (this.letterboxTop) this.el.removeChild(this.letterboxTop);
      if (this.letterboxBottom) this.el.removeChild(this.letterboxBottom);
      if (this.vignette) this.el.removeChild(this.vignette);
    }
  });


//---------------------------------------------------------------------------------------------

AFRAME.registerComponent('cinematic-camera-adv', {
  schema: {
    letterboxHeight: {type: 'number', default: 0.1},
    vignetteStrength: {type: 'number', default: 0.5},
    vignetteColor: {type: 'color', default: '#000000'},
    transitionDuration: {type: 'number', default: 1000},
    active: {type: 'boolean', default: true},
    bloomStrength: {type: 'number', default: 1.5},
    bloomRadius: {type: 'number', default: 0.4},
    bloomThreshold: {type: 'number', default: 0.85},
    taaEnabled: {type: 'boolean', default: true},
    colorGradingEnabled: {type: 'boolean', default: true},
    dofEnabled: {type: 'boolean', default: true},
    dofFocus: {type: 'number', default: 1.0},
    dofAperture: {type: 'number', default: 0.015},
    dofMaxBlur: {type: 'number', default: 1.0}
  },

  init: function() {
    this.camera = this.el.getObject3D('camera');
    this.scene = this.el.sceneEl.object3D;
    this.renderer = this.el.sceneEl.renderer;

    this.letterboxTop = null;
    this.letterboxBottom = null;
    this.vignette = null;

    this.createLetterbox();
    this.createVignette();

    this.setupPostProcessing();
    this.setupRenderOverride();

    this.updateEffect(this.data.active);
  },

  setupPostProcessing: function() {
    this.composer = new EffectComposer(this.renderer);

    // Define a custom luminance function to be used in all shaders
    const customLuminanceFn = `
      float luminance(vec3 color) {
        return dot(color, vec3(0.299, 0.587, 0.114));
      }
    `;

    this.renderPass = new RenderPass(this.scene, this.camera);
    this.composer.addPass(this.renderPass);

    this.taaPass = new TAARenderPass(this.scene, this.camera);
    this.taaPass.enabled = this.data.taaEnabled;
    this.composer.addPass(this.taaPass);

    this.bloomPass = new UnrealBloomPass(
      new THREE.Vector2(window.innerWidth, window.innerHeight),
      this.data.bloomStrength,
      this.data.bloomRadius,
      this.data.bloomThreshold
    );
    this.composer.addPass(this.bloomPass);

    this.colorGradingPass = new ShaderPass({
      uniforms: {
        tDiffuse: { value: null },
        brightness: { value: 0.05 },
        contrast: { value: 0.2 },
        saturation: { value: 0.1 },
        luminosityThreshold: { value: 0.8 },
        smoothWidth: { value: 0.01 }
      },
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        float luminance(vec3 color) {
          return dot(color, vec3(0.299, 0.587, 0.114));
        }
        uniform sampler2D tDiffuse;
        uniform float brightness;
        uniform float contrast;
        uniform float saturation;
        uniform float luminosityThreshold;
        uniform float smoothWidth;
        varying vec2 vUv;

        void main() {
          vec4 texel = texture2D(tDiffuse, vUv);
          vec3 color = texel.rgb;

          float v = dot(color, vec3(0.299, 0.587, 0.114));

          color = color + brightness;
          color = (color - 0.5) * (contrast + 1.0) + 0.5;
          vec3 grayscale = vec3(dot(color, vec3(0.299, 0.587, 0.114)));
          color = mix(grayscale, color, saturation + 1.0);

          float alpha = smoothstep(luminosityThreshold, luminosityThreshold + smoothWidth, v);

          gl_FragColor = vec4(mix(vec3(v), color, alpha), texel.a);
        }
      `
    });
    this.colorGradingPass.enabled = this.data.colorGradingEnabled;
    this.composer.addPass(this.colorGradingPass);

    this.bokehPass = new BokehPass(this.scene, this.camera, {
      focus: this.data.dofFocus,
      aperture: this.data.dofAperture,
      maxblur: this.data.dofMaxBlur
    });
    this.bokehPass.enabled = this.data.dofEnabled;
    this.composer.addPass(this.bokehPass);
  },

  setupRenderOverride: function() {
    const sceneEl = this.el.sceneEl;
    const renderer = sceneEl.renderer;

    this.originalRender = renderer.render;

    this.isRendering = false;

    renderer.render = (scene, camera) => {
      if (this.composer && this.data.active && !this.isRendering) {
        this.isRendering = true;
        this.composer.render();
        this.isRendering = false;
      } else {
        this.originalRender.call(renderer, scene, camera);
      }
    };

    this.renderStartListener = () => {
      if (this.composer && this.data.active && !this.isRendering) {
        this.isRendering = true;
        this.composer.render();
        this.isRendering = false;
      }
    };
    sceneEl.addEventListener('renderstart', this.renderStartListener);
  },

  update: function(oldData) {
    if (this.data.active !== oldData.active) {
      this.updateEffect(this.data.active);
    }
    if (this.data.letterboxHeight !== oldData.letterboxHeight) {
      this.updateLetterbox();
    }
    if (this.data.vignetteStrength !== oldData.vignetteStrength || 
        this.data.vignetteColor !== oldData.vignetteColor) {
      this.updateVignette();
    }
    if (this.data.bloomStrength !== oldData.bloomStrength ||
        this.data.bloomRadius !== oldData.bloomRadius ||
        this.data.bloomThreshold !== oldData.bloomThreshold) {
      this.updateBloom();
    }
    if (this.data.taaEnabled !== oldData.taaEnabled) {
      this.taaPass.enabled = this.data.taaEnabled;
    }
    if (this.data.colorGradingEnabled !== oldData.colorGradingEnabled) {
      this.colorGradingPass.enabled = this.data.colorGradingEnabled;
    }
    if (this.data.dofEnabled !== oldData.dofEnabled ||
        this.data.dofFocus !== oldData.dofFocus ||
        this.data.dofAperture !== oldData.dofAperture ||
        this.data.dofMaxBlur !== oldData.dofMaxBlur) {
      this.updateDepthOfField();
    }
  },

  createLetterbox: function() {
    this.letterboxTop = document.createElement('a-plane');
    this.letterboxBottom = document.createElement('a-plane');

    const commonAttributes = {
      color: '#000',
      opacity: 1,
      position: '0 0 -0.1',
      rotation: '0 0 0',
      material: 'shader: flat',
      width: 2,
      height: this.data.letterboxHeight
    };

    Object.keys(commonAttributes).forEach(attr => {
      this.letterboxTop.setAttribute(attr, commonAttributes[attr]);
      this.letterboxBottom.setAttribute(attr, commonAttributes[attr]);
    });

    this.updateLetterbox();

    this.el.appendChild(this.letterboxTop);
    this.el.appendChild(this.letterboxBottom);
  },

  updateLetterbox: function() {
    if (this.letterboxTop && this.letterboxBottom) {
      this.letterboxTop.setAttribute('position', `0 ${0.5 + this.data.letterboxHeight / 2} -0.1`);
      this.letterboxBottom.setAttribute('position', `0 ${-0.5 - this.data.letterboxHeight / 2} -0.1`);
      this.letterboxTop.setAttribute('geometry', 'height', this.data.letterboxHeight);
      this.letterboxBottom.setAttribute('geometry', 'height', this.data.letterboxHeight);
    }
  },

  createVignette: function() {
    this.vignette = document.createElement('a-entity');
    this.vignette.setAttribute('geometry', {
      primitive: 'plane',
      width: 2,
      height: 2
    });
    this.updateVignette();
    this.vignette.setAttribute('position', '0 0 -0.05');
    this.el.appendChild(this.vignette);
  },

  updateVignette: function() {
    if (this.vignette) {
      this.vignette.setAttribute('material', {
        shader: 'flat',
        src: this.createVignetteTexture(),
        transparent: true,
        opacity: this.data.vignetteStrength
      });
    }
  },

  createVignetteTexture: function() {
    const canvas = document.createElement('canvas');
    canvas.width = 256;
    canvas.height = 256;
    const ctx = canvas.getContext('2d');
    
    const gradient = ctx.createRadialGradient(128, 128, 0, 128, 128, 128);
    gradient.addColorStop(0, 'rgba(0,0,0,0)');
    gradient.addColorStop(1, this.data.vignetteColor);
    
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, 256, 256);
    
    return canvas.toDataURL();
  },

  updateBloom: function() {
    if (this.bloomPass) {
      this.bloomPass.strength = this.data.bloomStrength;
      this.bloomPass.radius = this.data.bloomRadius;
      this.bloomPass.threshold = this.data.bloomThreshold;
    }
  },

  updateDepthOfField: function() {
    if (this.bokehPass) {
      this.bokehPass.enabled = this.data.dofEnabled;
      if (this.data.dofEnabled) {
        this.bokehPass.uniforms["focus"].value = this.data.dofFocus;
        this.bokehPass.uniforms["aperture"].value = this.data.dofAperture;
        this.bokehPass.uniforms["maxblur"].value = this.data.dofMaxBlur;
      }
    }
  },

  updateEffect: function(active) {
    const elements = [this.letterboxTop, this.letterboxBottom, this.vignette];
    const targetOpacity = active ? 1 : 0;
    
    elements.forEach(el => {
      if (el) {
        el.setAttribute('animation__opacity', {
          property: 'material.opacity',
          to: targetOpacity,
          dur: this.data.transitionDuration,
          easing: 'easeInOutQuad'
        });
      }
    });

    if (this.bloomPass) this.bloomPass.enabled = active;
    if (this.taaPass) this.taaPass.enabled = active && this.data.taaEnabled;
    if (this.colorGradingPass) this.colorGradingPass.enabled = active && this.data.colorGradingEnabled;
    if (this.bokehPass) this.bokehPass.enabled = active && this.data.dofEnabled;
  },

  remove: function() {
    if (this.letterboxTop) this.el.removeChild(this.letterboxTop);
    if (this.letterboxBottom) this.el.removeChild(this.letterboxBottom);
    if (this.vignette) this.el.removeChild(this.vignette);
    
    if (this.el.sceneEl.renderer) {
      this.el.sceneEl.renderer.render = this.originalRender;
    }
    if (this.composer) {
      this.composer.dispose();
    }

    if (this.renderStartListener) {
      this.el.sceneEl.removeEventListener('renderstart', this.renderStartListener);
    }
  }
});

AFRAME.registerComponent('cinematic-camera-adv2', {
  schema: {
    letterboxHeight: {type: 'number', default: 0.15},       // Slightly increased for more cinematic look
    vignetteStrength: {type: 'number', default: 0.3},       // Reduced for subtlety
    vignetteColor: {type: 'color', default: '#000000'},     // Black is typically good for cinematic effect
    transitionDuration: {type: 'number', default: 1500},    // Slightly longer for smoother transitions
    active: {type: 'boolean', default: true},
    bloomStrength: {type: 'number', default: 0.8},          // Reduced for a more subtle glow
    bloomRadius: {type: 'number', default: 0.3},            // Slightly reduced for less spread
    bloomThreshold: {type: 'number', default: 0.9},         // Increased to affect only the brightest areas
    taaEnabled: {type: 'boolean', default: true},
    colorGradingEnabled: {type: 'boolean', default: true},
    dofEnabled: {type: 'boolean', default: true},
    dofFocus: {type: 'number', default: 3.0},               // Adjusted for medium-distance focus
    dofAperture: {type: 'number', default: 0.02},           // Slightly increased for more noticeable effect
    dofMaxBlur: {type: 'number', default: 0.8},             // Reduced for a more subtle blur
    renderScale: {type: 'number', default: 1.2}, 
  },

  init: function() {
    this.camera = this.el.getObject3D('camera');
    this.scene = this.el.sceneEl.object3D;
    this.renderer = this.el.sceneEl.renderer;

    this.letterboxTop = null;
    this.letterboxBottom = null;
    this.vignette = null;

    this.createLetterbox();
    this.createVignette();

    this.setupPostProcessing();
    this.setupRenderOverride();

    this.updateEffect(this.data.active);
  },

  setupPostProcessing: function() {
    const pixelRatio = this.renderer.getPixelRatio();
    const size = this.renderer.getSize(new THREE.Vector2());
    const renderWidth = size.width * this.data.renderScale;
    const renderHeight = size.height * this.data.renderScale;

    this.composer = new EffectComposer(this.renderer, new THREE.WebGLRenderTarget(renderWidth, renderHeight, {
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
      format: THREE.RGBAFormat,
      stencilBuffer: false
    }));

    const customLuminanceFn = `
      float luminance(vec3 color) {
        return dot(color, vec3(0.299, 0.587, 0.114));
      }
    `;

    this.renderPass = new RenderPass(this.scene, this.camera);
    this.composer.addPass(this.renderPass);

    this.taaPass = new TAARenderPass(this.scene, this.camera);
    this.taaPass.enabled = this.data.taaEnabled;
    this.composer.addPass(this.taaPass);

    this.bloomPass = new UnrealBloomPass(
      new THREE.Vector2(renderWidth, renderHeight),
      this.data.bloomStrength,
      this.data.bloomRadius,
      this.data.bloomThreshold
    );
    this.composer.addPass(this.bloomPass);

    this.colorGradingPass = new ShaderPass({
      uniforms: {
        tDiffuse: { value: null },
        brightness: { value: 0.05 },
        contrast: { value: 0.2 },
        saturation: { value: 0.1 },
        luminosityThreshold: { value: 0.8 },
        smoothWidth: { value: 0.01 }
      },
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        float luminance(vec3 color) {
          return dot(color, vec3(0.299, 0.587, 0.114));
        }
        uniform sampler2D tDiffuse;
        uniform float brightness;
        uniform float contrast;
        uniform float saturation;
        uniform float luminosityThreshold;
        uniform float smoothWidth;
        varying vec2 vUv;

void main() {
  vec4 texel = texture2D(tDiffuse, vUv);
  vec3 color = texel.rgb;

  // Apply brightness, contrast, and saturation
  color = color + brightness;
  color = (color - 0.5) * (contrast + 1.0) + 0.5;
  color = mix(vec3(luminance(color)), color, saturation + 1.0);

  // Apply color grading only to the luminance
  float v = luminance(color);
  float alpha = smoothstep(luminosityThreshold, luminosityThreshold + smoothWidth, v);

  // Mix between the original color and the color-graded version
  gl_FragColor = vec4(mix(color, vec3(v), alpha * 0.5), texel.a);
}
      `
    });
    this.colorGradingPass.enabled = this.data.colorGradingEnabled;
    this.composer.addPass(this.colorGradingPass);

    this.bokehPass = new BokehPass(this.scene, this.camera, {
      focus: this.data.dofFocus,
      aperture: this.data.dofAperture,
      maxblur: this.data.dofMaxBlur
    });
    this.bokehPass.enabled = this.data.dofEnabled;
    this.composer.addPass(this.bokehPass);

    this.upscalePass = new ShaderPass({
      uniforms: {
        tDiffuse: { value: null },
        resolution: { value: new THREE.Vector2(size.width * pixelRatio, size.height * pixelRatio) },
        renderResolution: { value: new THREE.Vector2(renderWidth, renderHeight) }
      },
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform sampler2D tDiffuse;
        uniform vec2 resolution;
        uniform vec2 renderResolution;
        varying vec2 vUv;

        void main() {
          vec2 ratio = vec2(
            min((resolution.x / resolution.y) / (renderResolution.x / renderResolution.y), 1.0),
            min((resolution.y / resolution.x) / (renderResolution.y / renderResolution.x), 1.0)
          );
          
          vec2 adjustedUv = vUv * ratio + (1.0 - ratio) * 0.5;
          gl_FragColor = texture2D(tDiffuse, adjustedUv);
        }
      `
    });
    this.composer.addPass(this.upscalePass);
  },

  setupRenderOverride: function() {
    const sceneEl = this.el.sceneEl;
    const renderer = sceneEl.renderer;

    this.originalRender = renderer.render;

    this.isRendering = false;

    renderer.render = (scene, camera) => {
      if (this.composer && this.data.active && !this.isRendering) {
        this.isRendering = true;
        this.composer.render();
        this.isRendering = false;
      } else {
        this.originalRender.call(renderer, scene, camera);
      }
    };

    this.renderStartListener = () => {
      if (this.composer && this.data.active && !this.isRendering) {
        this.isRendering = true;
        this.composer.render();
        this.isRendering = false;
      }
    };
    sceneEl.addEventListener('renderstart', this.renderStartListener);
  },

  update: function(oldData) {
    if (this.data.active !== oldData.active) {
      this.updateEffect(this.data.active);
    }
    if (this.data.letterboxHeight !== oldData.letterboxHeight) {
      this.updateLetterbox();
    }
    if (this.data.vignetteStrength !== oldData.vignetteStrength || 
        this.data.vignetteColor !== oldData.vignetteColor) {
      this.updateVignette();
    }
    if (this.data.bloomStrength !== oldData.bloomStrength ||
        this.data.bloomRadius !== oldData.bloomRadius ||
        this.data.bloomThreshold !== oldData.bloomThreshold) {
      this.updateBloom();
    }
    if (this.data.taaEnabled !== oldData.taaEnabled) {
      this.taaPass.enabled = this.data.taaEnabled;
    }
    if (this.data.colorGradingEnabled !== oldData.colorGradingEnabled) {
      this.colorGradingPass.enabled = this.data.colorGradingEnabled;
    }
    if (this.data.dofEnabled !== oldData.dofEnabled ||
        this.data.dofFocus !== oldData.dofFocus ||
        this.data.dofAperture !== oldData.dofAperture ||
        this.data.dofMaxBlur !== oldData.dofMaxBlur) {
      this.updateDepthOfField();
    }
    if (this.data.renderScale !== oldData.renderScale) {
      this.updateRenderScale();
    }
  },

  createLetterbox: function() {
    this.letterboxTop = document.createElement('a-plane');
    this.letterboxBottom = document.createElement('a-plane');

    const commonAttributes = {
      color: '#000',
      opacity: 1,
      position: '0 0 -0.1',
      rotation: '0 0 0',
      material: 'shader: flat',
      width: 2,
      height: this.data.letterboxHeight
    };

    Object.keys(commonAttributes).forEach(attr => {
      this.letterboxTop.setAttribute(attr, commonAttributes[attr]);
      this.letterboxBottom.setAttribute(attr, commonAttributes[attr]);
    });

    this.updateLetterbox();

    this.el.appendChild(this.letterboxTop);
    this.el.appendChild(this.letterboxBottom);
  },

  updateLetterbox: function() {
    if (this.letterboxTop && this.letterboxBottom) {
      this.letterboxTop.setAttribute('position', `0 ${0.5 + this.data.letterboxHeight / 2} -0.1`);
      this.letterboxBottom.setAttribute('position', `0 ${-0.5 - this.data.letterboxHeight / 2} -0.1`);
      this.letterboxTop.setAttribute('geometry', 'height', this.data.letterboxHeight);
      this.letterboxBottom.setAttribute('geometry', 'height', this.data.letterboxHeight);
    }
  },

  createVignette: function() {
    this.vignette = document.createElement('a-entity');
    this.vignette.setAttribute('geometry', {
      primitive: 'plane',
      width: 2,
      height: 2
    });
    this.updateVignette();
    this.vignette.setAttribute('position', '0 0 -0.05');
    this.el.appendChild(this.vignette);
  },

  updateVignette: function() {
    if (this.vignette) {
      this.vignette.setAttribute('material', {
        shader: 'flat',
        src: this.createVignetteTexture(),
        transparent: true,
        opacity: this.data.vignetteStrength
      });
    }
  },

  createVignetteTexture: function() {
    const canvas = document.createElement('canvas');
    canvas.width = 256;
    canvas.height = 256;
    const ctx = canvas.getContext('2d');
    
    const gradient = ctx.createRadialGradient(128, 128, 0, 128, 128, 128);
    gradient.addColorStop(0, 'rgba(0,0,0,0)');
    gradient.addColorStop(1, this.data.vignetteColor);
    
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, 256, 256);
    
    return canvas.toDataURL();
  },

  updateBloom: function() {
    if (this.bloomPass) {
      this.bloomPass.strength = this.data.bloomStrength;
      this.bloomPass.radius = this.data.bloomRadius;
      this.bloomPass.threshold = this.data.bloomThreshold;
    }
  },

  updateDepthOfField: function() {
    if (this.bokehPass) {
      this.bokehPass.enabled = this.data.dofEnabled;
      if (this.data.dofEnabled) {
        this.bokehPass.uniforms["focus"].value = this.data.dofFocus;
        this.bokehPass.uniforms["aperture"].value = this.data.dofAperture;
        this.bokehPass.uniforms["maxblur"].value = this.data.dofMaxBlur;
      }
    }
  },

  updateRenderScale: function() {
    if (this.composer && this.upscalePass) {
      const size = this.renderer.getSize(new THREE.Vector2());
      const pixelRatio = this.renderer.getPixelRatio();
      const renderWidth = size.width * this.data.renderScale;
      const renderHeight = size.height * this.data.renderScale;

      this.composer.setSize(renderWidth, renderHeight);
      this.upscalePass.uniforms.resolution.value.set(size.width * pixelRatio, size.height * pixelRatio);
      this.upscalePass.uniforms.renderResolution.value.set(renderWidth, renderHeight);

      if (this.bloomPass) {
        this.bloomPass.setSize(renderWidth, renderHeight);
      }
      if (this.bokehPass) {
        this.bokehPass.setSize(renderWidth, renderHeight);
      }
    }
  },

  updateEffect: function(active) {
    const elements = [this.letterboxTop, this.letterboxBottom, this.vignette];
    const targetOpacity = active ? 1 : 0;
    
    elements.forEach(el => {
      if (el) {
        el.setAttribute('animation__opacity', {
          property: 'material.opacity',
          to: targetOpacity,
          dur: this.data.transitionDuration,
          easing: 'easeInOutQuad'
        });
      }
    });

    if (this.bloomPass) this.bloomPass.enabled = active;
    if (this.taaPass) this.taaPass.enabled = active && this.data.taaEnabled;
    if (this.colorGradingPass) this.colorGradingPass.enabled = active && this.data.colorGradingEnabled;
    if (this.bokehPass) this.bokehPass.enabled = active && this.data.dofEnabled;
  },

  remove: function() {
    if (this.letterboxTop) this.el.removeChild(this.letterboxTop);
    if (this.letterboxBottom) this.el.removeChild(this.letterboxBottom);
    if (this.vignette) this.el.removeChild(this.vignette);
    
    if (this.el.sceneEl.renderer) {
      this.el.sceneEl.renderer.render = this.originalRender;
    }
    if (this.composer) {
      this.composer.dispose();
    }

    if (this.renderStartListener) {
      this.el.sceneEl.removeEventListener('renderstart', this.renderStartListener);
    }
  }
});









AFRAME.registerComponent('cinematic-camera-adv3', {
  schema: {
    letterboxHeight: {type: 'number', default: 0.15},       // Slightly increased for more cinematic look
    vignetteStrength: {type: 'number', default: 0.3},       // Reduced for subtlety
    vignetteColor: {type: 'color', default: '#000000'},     // Black is typically good for cinematic effect
    transitionDuration: {type: 'number', default: 1500},    // Slightly longer for smoother transitions
    active: {type: 'boolean', default: true},
    bloomStrength: {type: 'number', default: 0.8},          // Reduced for a more subtle glow
    bloomRadius: {type: 'number', default: 0.3},            // Slightly reduced for less spread
    bloomThreshold: {type: 'number', default: 0.9},         // Increased to affect only the brightest areas
    taaEnabled: {type: 'boolean', default: true},
    colorGradingEnabled: {type: 'boolean', default: true},
    dofEnabled: {type: 'boolean', default: true},
    dofFocus: {type: 'number', default: 3.0},               // Adjusted for medium-distance focus
    dofAperture: {type: 'number', default: 0.02},           // Slightly increased for more noticeable effect
    dofMaxBlur: {type: 'number', default: 0.8},             // Reduced for a more subtle blur
    renderScale: {type: 'number', default: 1.2}, 
    fxaaEnabled: {type: 'boolean', default: true},
    fxaaLevel: {type: 'number', default: 4, oneOf: [4, 8, 16]},
    casEnabled: {type: 'boolean', default: true},
    casSharpness: {type: 'number', default: 0.9, min: 0, max: 1},
    smaaEnabled: {type: 'boolean', default: true},
  },

  init: function() {
    this.camera = this.el.getObject3D('camera');
    this.scene = this.el.sceneEl.object3D;
    this.renderer = this.el.sceneEl.renderer;

    this.letterboxTop = null;
    this.letterboxBottom = null;
    this.vignette = null;

    this.createLetterbox();
    this.createVignette();

    this.setupPostProcessing();
    this.setupRenderOverride();

    //this.onResize = this.onResize.bind(this);
    //window.addEventListener('resize', this.onResize);

    this.updateEffect(this.data.active);
  },

  onResize: function() {
    if (!this.composer) return;

    const canvas = this.el.sceneEl.canvas;
    const pixelRatio = this.renderer.getPixelRatio();
    const width = canvas.clientWidth * pixelRatio * this.data.renderScale;
    const height = canvas.clientHeight * pixelRatio * this.data.renderScale;

    this.composer.setSize(width, height);

    if (this.smaaPass) {
      this.smaaPass.setSize(width, height);
    }
    if (this.fxaaPass) {
      this.updateFXAA();
    }
    if (this.bloomPass) {
      this.bloomPass.setSize(width, height);
    }
    if (this.bokehPass) {
      this.bokehPass.setSize(width, height);
    }
    if (this.upscalePass) {
      this.updateRenderScale();
    }

    // Update camera aspect ratio and projection matrix
    const camera = this.el.getObject3D('camera');
    if (camera) {
      camera.aspect = canvas.clientWidth / canvas.clientHeight;
      camera.updateProjectionMatrix();
    }

    // Force a single render to update the view
    this.el.sceneEl.renderer.render(this.el.sceneEl.object3D, camera);
  },

  setupPostProcessing: function() {
    const pixelRatio = this.renderer.getPixelRatio();
    const size = this.renderer.getSize(new THREE.Vector2());
    const renderWidth = size.width * this.data.renderScale;
    const renderHeight = size.height * this.data.renderScale;

    this.composer = new EffectComposer(this.renderer, new THREE.WebGLRenderTarget(renderWidth, renderHeight, {
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
      format: THREE.RGBAFormat,
      stencilBuffer: false
    }));

    const customLuminanceFn = `
      float luminance(vec3 color) {
        return dot(color, vec3(0.299, 0.587, 0.114));
      }
    `;

    this.renderPass = new RenderPass(this.scene, this.camera);
    this.composer.addPass(this.renderPass);

    this.fxaaPass = new ShaderPass(FXAAShader);
    this.fxaaPass.material.uniforms['resolution'].value = new THREE.Vector2();
    this.fxaaPass.material.uniforms['fxaaQualityRcpFrame'] = { value: new THREE.Vector2() };
    this.fxaaPass.enabled = this.data.fxaaEnabled;
    this.composer.addPass(this.fxaaPass);
  
    this.updateFXAA();

    this.createCASPass();

    const width = this.el.sceneEl.canvas.width * pixelRatio;
    const height = this.el.sceneEl.canvas.height * pixelRatio;
    this.smaaPass = new SMAAPass(width, height);
    this.smaaPass.enabled = this.data.smaaEnabled;
    this.composer.addPass(this.smaaPass);

    this.bloomPass = new UnrealBloomPass(
      new THREE.Vector2(renderWidth, renderHeight),
      this.data.bloomStrength,
      this.data.bloomRadius,
      this.data.bloomThreshold
    );
    this.composer.addPass(this.bloomPass);

    this.colorGradingPass = new ShaderPass({
      uniforms: {
        tDiffuse: { value: null },
        brightness: { value: 0.05 },
        contrast: { value: 0.2 },
        saturation: { value: 0.1 },
        luminosityThreshold: { value: 0.8 },
        smoothWidth: { value: 0.01 }
      },
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        float luminance(vec3 color) {
          return dot(color, vec3(0.299, 0.587, 0.114));
        }
        uniform sampler2D tDiffuse;
        uniform float brightness;
        uniform float contrast;
        uniform float saturation;
        uniform float luminosityThreshold;
        uniform float smoothWidth;
        varying vec2 vUv;

void main() {
  vec4 texel = texture2D(tDiffuse, vUv);
  vec3 color = texel.rgb;

  // Apply brightness, contrast, and saturation
  color = color + brightness;
  color = (color - 0.5) * (contrast + 1.0) + 0.5;
  color = mix(vec3(luminance(color)), color, saturation + 1.0);

  // Apply color grading only to the luminance
  float v = luminance(color);
  float alpha = smoothstep(luminosityThreshold, luminosityThreshold + smoothWidth, v);

  // Mix between the original color and the color-graded version
  gl_FragColor = vec4(mix(color, vec3(v), alpha * 0.5), texel.a);
}
      `
    });
    this.colorGradingPass.enabled = this.data.colorGradingEnabled;
    this.composer.addPass(this.colorGradingPass);

    this.bokehPass = new BokehPass(this.scene, this.camera, {
      focus: this.data.dofFocus,
      aperture: this.data.dofAperture,
      maxblur: this.data.dofMaxBlur
    });
    this.bokehPass.enabled = this.data.dofEnabled;
    this.composer.addPass(this.bokehPass);

    this.upscalePass = new ShaderPass({
      uniforms: {
        tDiffuse: { value: null },
        resolution: { value: new THREE.Vector2(size.width * pixelRatio, size.height * pixelRatio) },
        renderResolution: { value: new THREE.Vector2(renderWidth, renderHeight) }
      },
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform sampler2D tDiffuse;
        uniform vec2 resolution;
        uniform vec2 renderResolution;
        varying vec2 vUv;

        void main() {
          vec2 ratio = vec2(
            min((resolution.x / resolution.y) / (renderResolution.x / renderResolution.y), 1.0),
            min((resolution.y / resolution.x) / (renderResolution.y / renderResolution.x), 1.0)
          );
          
          vec2 adjustedUv = vUv * ratio + (1.0 - ratio) * 0.5;
          gl_FragColor = texture2D(tDiffuse, adjustedUv);
        }
      `
    });
    this.composer.addPass(this.upscalePass);
  },

  setupRenderOverride: function() {
    const sceneEl = this.el.sceneEl;
    const renderer = sceneEl.renderer;

    this.originalRender = renderer.render;

    this.isRendering = false;

    renderer.render = (scene, camera) => {
      if (this.composer && this.data.active && !this.isRendering) {
        this.isRendering = true;
        this.composer.render();
        this.isRendering = false;
      } else {
        this.originalRender.call(renderer, scene, camera);
      }
    };

    this.renderStartListener = () => {
      if (this.composer && this.data.active && !this.isRendering) {
        this.isRendering = true;
        this.composer.render();
        this.isRendering = false;
      }
    };
    sceneEl.addEventListener('renderstart', this.renderStartListener);
  },

  update: function(oldData) {
    if (this.data.active !== oldData.active) {
      this.updateEffect(this.data.active);
    }
    if (this.data.letterboxHeight !== oldData.letterboxHeight) {
      this.updateLetterbox();
    }
    if (this.data.vignetteStrength !== oldData.vignetteStrength || 
        this.data.vignetteColor !== oldData.vignetteColor) {
      this.updateVignette();
    }
    if (this.data.bloomStrength !== oldData.bloomStrength ||
        this.data.bloomRadius !== oldData.bloomRadius ||
        this.data.bloomThreshold !== oldData.bloomThreshold) {
      this.updateBloom();
    }
    if (this.data.fxaaEnabled !== oldData.fxaaEnabled ||
      this.data.fxaaLevel !== oldData.fxaaLevel) {
    this.updateFXAA();
  }

  if (this.data.smaaEnabled !== oldData.smaaEnabled) {
    this.updateSMAA();
  }

  if (this.data.casEnabled !== oldData.casEnabled ||
    this.data.casSharpness !== oldData.casSharpness) {
  this.updateCAS();
}
    if (this.data.colorGradingEnabled !== oldData.colorGradingEnabled) {
      this.colorGradingPass.enabled = this.data.colorGradingEnabled;
    }
    if (this.data.dofEnabled !== oldData.dofEnabled ||
        this.data.dofFocus !== oldData.dofFocus ||
        this.data.dofAperture !== oldData.dofAperture ||
        this.data.dofMaxBlur !== oldData.dofMaxBlur) {
      this.updateDepthOfField();
    }
    if (this.data.renderScale !== oldData.renderScale) {
      this.updateRenderScale();
    }
  },

  createLetterbox: function() {
    this.letterboxTop = document.createElement('a-plane');
    this.letterboxBottom = document.createElement('a-plane');

    const commonAttributes = {
      color: '#000',
      opacity: 1,
      position: '0 0 -0.1',
      rotation: '0 0 0',
      material: 'shader: flat',
      width: 2,
      height: this.data.letterboxHeight
    };

    Object.keys(commonAttributes).forEach(attr => {
      this.letterboxTop.setAttribute(attr, commonAttributes[attr]);
      this.letterboxBottom.setAttribute(attr, commonAttributes[attr]);
    });

    this.updateLetterbox();

    this.el.appendChild(this.letterboxTop);
    this.el.appendChild(this.letterboxBottom);
  },

  updateFXAA: function() {
    if (this.fxaaPass && this.fxaaPass.material) {
      this.fxaaPass.enabled = this.data.fxaaEnabled;
      if (this.data.fxaaEnabled) {
        const pixelRatio = this.renderer.getPixelRatio();
        const width = this.el.sceneEl.canvas.width;
        const height = this.el.sceneEl.canvas.height;
        
        this.fxaaPass.material.uniforms['resolution'].value.set(1 / (width * pixelRatio), 1 / (height * pixelRatio));
        
        // Adjust FXAA quality based on level
        let qualityValue;
        switch (this.data.fxaaLevel) {
          case 4:
            qualityValue = 1/4;
            break;
          case 8:
            qualityValue = 1/8;
            break;
          case 16:
            qualityValue = 1/16;
            break;
          default:
            qualityValue = 1/4;
        }
        this.fxaaPass.material.uniforms['fxaaQualityRcpFrame'].value.set(qualityValue, qualityValue);
      }
    }
  },

  updateLetterbox: function() {
    if (this.letterboxTop && this.letterboxBottom) {
      this.letterboxTop.setAttribute('position', `0 ${0.5 + this.data.letterboxHeight / 2} -0.1`);
      this.letterboxBottom.setAttribute('position', `0 ${-0.5 - this.data.letterboxHeight / 2} -0.1`);
      this.letterboxTop.setAttribute('geometry', 'height', this.data.letterboxHeight);
      this.letterboxBottom.setAttribute('geometry', 'height', this.data.letterboxHeight);
    }
  },

  updateSMAA: function() {
    if (this.smaaPass) {
      this.smaaPass.enabled = this.data.smaaEnabled;
    }
  },

  createCASPass: function() {
    const CASShader = {
      uniforms: {
        "tDiffuse": { value: null },
        "sharpness": { value: 0.5 }
      },
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
        }
      `,
      fragmentShader: `
        uniform sampler2D tDiffuse;
        uniform float sharpness;
        varying vec2 vUv;
  
        void main() {
          vec2 texSize = vec2(textureSize(tDiffuse, 0));
          vec2 texelSize = 1.0 / texSize;
          
          vec3 col = texture2D(tDiffuse, vUv).rgb;
          vec3 topLeft = texture2D(tDiffuse, vUv + texelSize * vec2(-1, -1)).rgb;
          vec3 topRight = texture2D(tDiffuse, vUv + texelSize * vec2(1, -1)).rgb;
          vec3 bottomLeft = texture2D(tDiffuse, vUv + texelSize * vec2(-1, 1)).rgb;
          vec3 bottomRight = texture2D(tDiffuse, vUv + texelSize * vec2(1, 1)).rgb;
          
          vec3 minColor = min(min(topLeft, topRight), min(bottomLeft, bottomRight));
          vec3 maxColor = max(max(topLeft, topRight), max(bottomLeft, bottomRight));
          
          vec3 sharpened = clamp(col + (col - 0.5 * (minColor + maxColor)) * sharpness, minColor, maxColor);
          
          gl_FragColor = vec4(sharpened, 1.0);
        }
      `
    };
  
    this.casPass = new ShaderPass(CASShader);
    this.casPass.enabled = this.data.casEnabled;
    this.casPass.uniforms.sharpness.value = this.data.casSharpness;
    this.composer.addPass(this.casPass);
  },

  updateCAS: function() {
    if (this.casPass) {
      this.casPass.enabled = this.data.casEnabled;
      this.casPass.uniforms.sharpness.value = this.data.casSharpness;
    }
  },

  createVignette: function() {
    this.vignette = document.createElement('a-entity');
    this.vignette.setAttribute('geometry', {
      primitive: 'plane',
      width: 2,
      height: 2
    });
    this.updateVignette();
    this.vignette.setAttribute('position', '0 0 -0.05');
    this.el.appendChild(this.vignette);
  },

  updateVignette: function() {
    if (this.vignette) {
      this.vignette.setAttribute('material', {
        shader: 'flat',
        src: this.createVignetteTexture(),
        transparent: true,
        opacity: this.data.vignetteStrength
      });
    }
  },

  createVignetteTexture: function() {
    const canvas = document.createElement('canvas');
    canvas.width = 256;
    canvas.height = 256;
    const ctx = canvas.getContext('2d');
    
    const gradient = ctx.createRadialGradient(128, 128, 0, 128, 128, 128);
    gradient.addColorStop(0, 'rgba(0,0,0,0)');
    gradient.addColorStop(1, this.data.vignetteColor);
    
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, 256, 256);
    
    return canvas.toDataURL();
  },

  updateBloom: function() {
    if (this.bloomPass) {
      this.bloomPass.strength = this.data.bloomStrength;
      this.bloomPass.radius = this.data.bloomRadius;
      this.bloomPass.threshold = this.data.bloomThreshold;
    }
  },

  updateDepthOfField: function() {
    if (this.bokehPass) {
      this.bokehPass.enabled = this.data.dofEnabled;
      if (this.data.dofEnabled) {
        this.bokehPass.uniforms["focus"].value = this.data.dofFocus;
        this.bokehPass.uniforms["aperture"].value = this.data.dofAperture;
        this.bokehPass.uniforms["maxblur"].value = this.data.dofMaxBlur;
      }
    }
  },

  updateRenderScale: function() {
    if (this.composer && this.upscalePass) {
      const size = this.renderer.getSize(new THREE.Vector2());
      const pixelRatio = this.renderer.getPixelRatio();
      const renderWidth = size.width * this.data.renderScale;
      const renderHeight = size.height * this.data.renderScale;

      this.composer.setSize(renderWidth, renderHeight);
      this.upscalePass.uniforms.resolution.value.set(size.width * pixelRatio, size.height * pixelRatio);
      this.upscalePass.uniforms.renderResolution.value.set(renderWidth, renderHeight);

      if (this.bloomPass) {
        this.bloomPass.setSize(renderWidth, renderHeight);
      }
      if (this.bokehPass) {
        this.bokehPass.setSize(renderWidth, renderHeight);
      }
      if (this.fxaaPass) {
        this.updateFXAA();
      }
      if (this.smaaPass) {
        this.smaaPass.setSize(renderWidth, renderHeight);
      }
    }
  },

  updateEffect: function(active) {
    const elements = [this.letterboxTop, this.letterboxBottom, this.vignette];
    const targetOpacity = active ? 1 : 0;
    
    elements.forEach(el => {
      if (el) {
        el.setAttribute('animation__opacity', {
          property: 'material.opacity',
          to: targetOpacity,
          dur: this.data.transitionDuration,
          easing: 'easeInOutQuad'
        });
      }
    });
    if (this.smaaPass) this.smaaPass.enabled = active && this.data.smaaEnabled;
    if (this.bloomPass) this.bloomPass.enabled = active;
    if (this.taaPass) this.taaPass.enabled = active && this.data.taaEnabled;
    if (this.colorGradingPass) this.colorGradingPass.enabled = active && this.data.colorGradingEnabled;
    if (this.bokehPass) this.bokehPass.enabled = active && this.data.dofEnabled;
    if (this.fxaaPass) this.fxaaPass.enabled = active && this.data.fxaaEnabled;

  },

  remove: function() {
    if (this.letterboxTop) this.el.removeChild(this.letterboxTop);
    if (this.letterboxBottom) this.el.removeChild(this.letterboxBottom);
    if (this.vignette) this.el.removeChild(this.vignette);
    window.removeEventListener('resize', this.onResize);

    if (this.fxaaPass) {
      this.composer.removePass(this.fxaaPass);
    }
    
    if (this.el.sceneEl.renderer) {
      this.el.sceneEl.renderer.render = this.originalRender;
    }
    if (this.composer) {
      this.composer.dispose();
    }

    if (this.smaaPass) {
      this.composer.removePass(this.smaaPass);
    }

    if (this.renderStartListener) {
      this.el.sceneEl.removeEventListener('renderstart', this.renderStartListener);
    }
  }
});





AFRAME.registerComponent('cinematic-camera-adv4', {
  schema: {
    letterboxHeight: {type: 'number', default: 0.15},
    vignetteStrength: {type: 'number', default: 0.3},
    vignetteColor: {type: 'color', default: '#000000'},
    transitionDuration: {type: 'number', default: 1500},
    active: {type: 'boolean', default: true},
    bloomStrength: {type: 'number', default: 0.8},
    bloomRadius: {type: 'number', default: 0.3},
    bloomThreshold: {type: 'number', default: 0.9},
    taaEnabled: {type: 'boolean', default: true},
    colorGradingEnabled: {type: 'boolean', default: true},
    dofEnabled: {type: 'boolean', default: true},
    dofFocus: {type: 'number', default: 3.0},
    dofAperture: {type: 'number', default: 0.02},
    dofMaxBlur: {type: 'number', default: 0.8},
    renderScale: {type: 'number', default: 1.2},
    fxaaEnabled: {type: 'boolean', default: true},
    fxaaLevel: {type: 'number', default: 4, oneOf: [4, 8, 16]},
    casEnabled: {type: 'boolean', default: true},
    casSharpness: {type: 'number', default: 0.9, min: 0, max: 1},
    smaaEnabled: {type: 'boolean', default: true},
    msaaEnabled: {type: 'boolean', default: false},
    msaaSamples: {type: 'number', default: 4, oneOf: [2, 4, 8, 16]}
  },

  init: function() {
    this.camera = this.el.getObject3D('camera');
    this.scene = this.el.sceneEl.object3D;
    this.renderer = this.el.sceneEl.renderer;

    this.letterboxTop = null;
    this.letterboxBottom = null;
    this.vignette = null;

    this.createLetterbox();
    this.createVignette();

    this.setupPostProcessing();
    this.setupRenderOverride();

    this.updateEffect(this.data.active);
  },

  onResize: function() {
    if (!this.composer) return;

    const canvas = this.el.sceneEl.canvas;
    const pixelRatio = this.renderer.getPixelRatio();
    const width = canvas.clientWidth * pixelRatio * this.data.renderScale;
    const height = canvas.clientHeight * pixelRatio * this.data.renderScale;

    this.composer.setSize(width, height);

    if (this.smaaPass) this.smaaPass.setSize(width, height);
    if (this.fxaaPass) this.updateFXAA();
    if (this.bloomPass) this.bloomPass.setSize(width, height);
    if (this.bokehPass) this.bokehPass.setSize(width, height);
    if (this.upscalePass) this.updateRenderScale();

    const camera = this.el.getObject3D('camera');
    if (camera) {
      camera.aspect = canvas.clientWidth / canvas.clientHeight;
      camera.updateProjectionMatrix();
    }

    this.el.sceneEl.renderer.render(this.el.sceneEl.object3D, camera);
  },

  setupPostProcessing: function() {
    const pixelRatio = this.renderer.getPixelRatio();
    const size = this.renderer.getSize(new THREE.Vector2());
    const renderWidth = size.width * this.data.renderScale;
    const renderHeight = size.height * this.data.renderScale;

    const renderTarget = new THREE.WebGLRenderTarget(renderWidth, renderHeight, {
      minFilter: THREE.LinearFilter,
      magFilter: THREE.LinearFilter,
      format: THREE.RGBAFormat,
      stencilBuffer: false
  });

  if (this.data.msaaEnabled) {
      renderTarget.samples = this.data.msaaSamples;
  }

    this.composer = new EffectComposer(this.renderer, renderTarget);

    this.renderPass = new RenderPass(this.scene, this.camera);
    this.composer.addPass(this.renderPass);

    this.fxaaPass = new ShaderPass(FXAAShader);
    this.fxaaPass.material.uniforms['resolution'].value = new THREE.Vector2();
    this.fxaaPass.material.uniforms['fxaaQualityRcpFrame'] = { value: new THREE.Vector2() };
    this.fxaaPass.enabled = this.data.fxaaEnabled;
    this.composer.addPass(this.fxaaPass);

    this.updateFXAA();

    this.createCASPass();

    const width = this.el.sceneEl.canvas.width * pixelRatio;
    const height = this.el.sceneEl.canvas.height * pixelRatio;
    this.smaaPass = new SMAAPass(width, height);
    this.smaaPass.enabled = this.data.smaaEnabled;
    this.composer.addPass(this.smaaPass);

    this.bloomPass = new UnrealBloomPass(
      new THREE.Vector2(renderWidth, renderHeight),
      this.data.bloomStrength,
      this.data.bloomRadius,
      this.data.bloomThreshold
    );
    this.composer.addPass(this.bloomPass);

    this.colorGradingPass = new ShaderPass({
      uniforms: {
        tDiffuse: { value: null },
        brightness: { value: 0.05 },
        contrast: { value: 0.2 },
        saturation: { value: 0.1 },
        luminosityThreshold: { value: 0.8 },
        smoothWidth: { value: 0.01 }
      },
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        float luminance(vec3 color) {
          return dot(color, vec3(0.299, 0.587, 0.114));
        }
        uniform sampler2D tDiffuse;
        uniform float brightness;
        uniform float contrast;
        uniform float saturation;
        uniform float luminosityThreshold;
        uniform float smoothWidth;
        varying vec2 vUv;

        void main() {
          vec4 texel = texture2D(tDiffuse, vUv);
          vec3 color = texel.rgb;

          color = color + brightness;
          color = (color - 0.5) * (contrast + 1.0) + 0.5;
          color = mix(vec3(luminance(color)), color, saturation + 1.0);

          float v = luminance(color);
          float alpha = smoothstep(luminosityThreshold, luminosityThreshold + smoothWidth, v);

          gl_FragColor = vec4(mix(color, vec3(v), alpha * 0.5), texel.a);
        }
      `
    });
    this.colorGradingPass.enabled = this.data.colorGradingEnabled;
    this.composer.addPass(this.colorGradingPass);

    this.bokehPass = new BokehPass(this.scene, this.camera, {
      focus: this.data.dofFocus,
      aperture: this.data.dofAperture,
      maxblur: this.data.dofMaxBlur
    });
    this.bokehPass.enabled = this.data.dofEnabled;
    this.composer.addPass(this.bokehPass);

    this.upscalePass = new ShaderPass({
      uniforms: {
        tDiffuse: { value: null },
        resolution: { value: new THREE.Vector2(size.width * pixelRatio, size.height * pixelRatio) },
        renderResolution: { value: new THREE.Vector2(renderWidth, renderHeight) }
      },
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform sampler2D tDiffuse;
        uniform vec2 resolution;
        uniform vec2 renderResolution;
        varying vec2 vUv;

        void main() {
          vec2 ratio = vec2(
            min((resolution.x / resolution.y) / (renderResolution.x / renderResolution.y), 1.0),
            min((resolution.y / resolution.x) / (renderResolution.y / renderResolution.x), 1.0)
          );

          vec2 adjustedUv = vUv * ratio + (1.0 - ratio) * 0.5;
          gl_FragColor = texture2D(tDiffuse, adjustedUv);
        }
      `
    });
    this.composer.addPass(this.upscalePass);
  },

  setupRenderOverride: function() {
    const sceneEl = this.el.sceneEl;
    const renderer = sceneEl.renderer;

    this.originalRender = renderer.render;

    this.isRendering = false;

    renderer.render = (scene, camera) => {
      if (this.composer && this.data.active && !this.isRendering) {
        this.isRendering = true;
        this.composer.render();
        this.isRendering = false;
      } else {
        this.originalRender.call(renderer, scene, camera);
      }
    };

    this.renderStartListener = () => {
      if (this.composer && this.data.active && !this.isRendering) {
        this.isRendering = true;
        this.composer.render();
        this.isRendering = false;
      }
    };
    sceneEl.addEventListener('renderstart', this.renderStartListener);
  },

  update: function(oldData) {
    if (this.data.active !== oldData.active) this.updateEffect(this.data.active);
    if (this.data.letterboxHeight !== oldData.letterboxHeight) this.updateLetterbox();
    if (this.data.vignetteStrength !== oldData.vignetteStrength || this.data.vignetteColor !== oldData.vignetteColor) this.updateVignette();
    if (this.data.bloomStrength !== oldData.bloomStrength || this.data.bloomRadius !== oldData.bloomRadius || this.data.bloomThreshold !== oldData.bloomThreshold) this.updateBloom();
    if (this.data.fxaaEnabled !== oldData.fxaaEnabled || this.data.fxaaLevel !== oldData.fxaaLevel) this.updateFXAA();
    if (this.data.smaaEnabled !== oldData.smaaEnabled) this.updateSMAA();
    if (this.data.casEnabled !== oldData.casEnabled || this.data.casSharpness !== oldData.casSharpness) this.updateCAS();
    if (this.data.colorGradingEnabled !== oldData.colorGradingEnabled) this.colorGradingPass.enabled = this.data.colorGradingEnabled;
    if (this.data.dofEnabled !== oldData.dofEnabled || this.data.dofFocus !== oldData.dofFocus || this.data.dofAperture !== oldData.dofAperture || this.data.dofMaxBlur !== oldData.dofMaxBlur) this.updateDepthOfField();
    if (this.data.renderScale !== oldData.renderScale) this.updateRenderScale();
    if (this.data.msaaEnabled !== oldData.msaaEnabled || this.data.msaaSamples !== oldData.msaaSamples) this.setupPostProcessing();
  },

  createLetterbox: function() {
    this.letterboxTop = document.createElement('a-plane');
    this.letterboxBottom = document.createElement('a-plane');

    const commonAttributes = {
      color: '#000',
      opacity: 1,
      position: '0 0 -0.1',
      rotation: '0 0 0',
      material: 'shader: flat',
      width: 2,
      height: this.data.letterboxHeight
    };

    Object.keys(commonAttributes).forEach(attr => {
      this.letterboxTop.setAttribute(attr, commonAttributes[attr]);
      this.letterboxBottom.setAttribute(attr, commonAttributes[attr]);
    });

    this.updateLetterbox();

    this.el.appendChild(this.letterboxTop);
    this.el.appendChild(this.letterboxBottom);
  },

  updateFXAA: function() {
    if (this.fxaaPass && this.fxaaPass.material) {
      this.fxaaPass.enabled = this.data.fxaaEnabled;
      if (this.data.fxaaEnabled) {
        const pixelRatio = this.renderer.getPixelRatio();
        const width = this.el.sceneEl.canvas.width;
        const height = this.el.sceneEl.canvas.height;

        this.fxaaPass.material.uniforms['resolution'].value.set(1 / (width * pixelRatio), 1 / (height * pixelRatio));

        let qualityValue;
        switch (this.data.fxaaLevel) {
          case 4:
            qualityValue = 1/4;
            break;
          case 8:
            qualityValue = 1/8;
            break;
          case 16:
            qualityValue = 1/16;
            break;
          default:
            qualityValue = 1/4;
        }
        this.fxaaPass.material.uniforms['fxaaQualityRcpFrame'].value.set(qualityValue, qualityValue);
      }
    }
  },

  updateSMAA: function() {
    if (this.smaaPass) this.smaaPass.enabled = this.data.smaaEnabled;
  },

  createCASPass: function() {
    const CASShader = {
      uniforms: {
        "tDiffuse": { value: null },
        "sharpness": { value: 0.5 }
      },
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
        }
      `,
      fragmentShader: `
        uniform sampler2D tDiffuse;
        uniform float sharpness;
        varying vec2 vUv;

        void main() {
          vec2 texSize = vec2(textureSize(tDiffuse, 0));
          vec2 texelSize = 1.0 / texSize;

          vec3 col = texture2D(tDiffuse, vUv).rgb;
          vec3 topLeft = texture2D(tDiffuse, vUv + texelSize * vec2(-1, -1)).rgb;
          vec3 topRight = texture2D(tDiffuse, vUv + texelSize * vec2(1, -1)).rgb;
          vec3 bottomLeft = texture2D(tDiffuse, vUv + texelSize * vec2(-1, 1)).rgb;
          vec3 bottomRight = texture2D(tDiffuse, vUv + texelSize * vec2(1, 1)).rgb;

          vec3 minColor = min(min(topLeft, topRight), min(bottomLeft, bottomRight));
          vec3 maxColor = max(max(topLeft, topRight), max(bottomLeft, bottomRight));

          vec3 sharpened = clamp(col + (col - 0.5 * (minColor + maxColor)) * sharpness, minColor, maxColor);

          gl_FragColor = vec4(sharpened, 1.0);
        }
      `
    };

    this.casPass = new ShaderPass(CASShader);
    this.casPass.enabled = this.data.casEnabled;
    this.casPass.uniforms.sharpness.value = this.data.casSharpness;
    this.composer.addPass(this.casPass);
  },

  updateCAS: function() {
    if (this.casPass) {
      this.casPass.enabled = this.data.casEnabled;
      this.casPass.uniforms.sharpness.value = this.data.casSharpness;
    }
  },

  updateLetterbox: function() {
    if (this.letterboxTop && this.letterboxBottom) {
      this.letterboxTop.setAttribute('position', `0 ${0.5 + this.data.letterboxHeight / 2} -0.1`);
      this.letterboxBottom.setAttribute('position', `0 ${-0.5 - this.data.letterboxHeight / 2} -0.1`);
      this.letterboxTop.setAttribute('geometry', 'height', this.data.letterboxHeight);
      this.letterboxBottom.setAttribute('geometry', 'height', this.data.letterboxHeight);
    }
  },


  createVignette: function() {
    this.vignette = document.createElement('a-entity');
    this.vignette.setAttribute('geometry', {
      primitive: 'plane',
      width: 2,
      height: 2
    });
    this.updateVignette();
    this.vignette.setAttribute('position', '0 0 -0.05');
    this.el.appendChild(this.vignette);
  },

  updateVignette: function() {
    if (this.vignette) {
      this.vignette.setAttribute('material', {
        shader: 'flat',
        src: this.createVignetteTexture(),
        transparent: true,
        opacity: this.data.vignetteStrength
      });
    }
  },

  createVignetteTexture: function() {
    const canvas = document.createElement('canvas');
    canvas.width = 256;
    canvas.height = 256;
    const ctx = canvas.getContext('2d');

    const gradient = ctx.createRadialGradient(128, 128, 0, 128, 128, 128);
    gradient.addColorStop(0, 'rgba(0,0,0,0)');
    gradient.addColorStop(1, this.data.vignetteColor);

    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, 256, 256);

    return canvas.toDataURL();
  },

  updateBloom: function() {
    if (this.bloomPass) {
      this.bloomPass.strength = this.data.bloomStrength;
      this.bloomPass.radius = this.data.bloomRadius;
      this.bloomPass.threshold = this.data.bloomThreshold;
    }
  },

  updateDepthOfField: function() {
    if (this.bokehPass) {
      this.bokehPass.enabled = this.data.dofEnabled;
      if (this.data.dofEnabled) {
        this.bokehPass.uniforms["focus"].value = this.data.dofFocus;
        this.bokehPass.uniforms["aperture"].value = this.data.dofAperture;
        this.bokehPass.uniforms["maxblur"].value = this.data.dofMaxBlur;
      }
    }
  },

  updateRenderScale: function() {
    if (this.composer && this.upscalePass) {
      const size = this.renderer.getSize(new THREE.Vector2());
      const pixelRatio = this.renderer.getPixelRatio();
      const renderWidth = size.width * this.data.renderScale;
      const renderHeight = size.height * this.data.renderScale;

      this.composer.setSize(renderWidth, renderHeight);
      this.upscalePass.uniforms.resolution.value.set(size.width * pixelRatio, size.height * pixelRatio);
      this.upscalePass.uniforms.renderResolution.value.set(renderWidth, renderHeight);

      if (this.bloomPass) this.bloomPass.setSize(renderWidth, renderHeight);
      if (this.bokehPass) this.bokehPass.setSize(renderWidth, renderHeight);
      if (this.fxaaPass) this.updateFXAA();
      if (this.smaaPass) this.smaaPass.setSize(renderWidth, renderHeight);
    }
  },

  updateEffect: function(active) {
    const elements = [this.letterboxTop, this.letterboxBottom, this.vignette];
    const targetOpacity = active ? 1 : 0;

    elements.forEach(el => {
      if (el) {
        el.setAttribute('animation__opacity', {
          property: 'material.opacity',
          to: targetOpacity,
          dur: this.data.transitionDuration,
          easing: 'easeInOutQuad'
        });
      }
    });
    if (this.smaaPass) this.smaaPass.enabled = active && this.data.smaaEnabled;
    if (this.bloomPass) this.bloomPass.enabled = active;
    if (this.taaPass) this.taaPass.enabled = active && this.data.taaEnabled;
    if (this.colorGradingPass) this.colorGradingPass.enabled = active && this.data.colorGradingEnabled;
    if (this.bokehPass) this.bokehPass.enabled = active && this.data.dofEnabled;
    if (this.fxaaPass) this.fxaaPass.enabled = active && this.data.fxaaEnabled;
  },

  remove: function() {
    if (this.letterboxTop) this.el.removeChild(this.letterboxTop);
    if (this.letterboxBottom) this.el.removeChild(this.letterboxBottom);
    if (this.vignette) this.el.removeChild(this.vignette);
    window.removeEventListener('resize', this.onResize);

    if (this.fxaaPass) {
      this.composer.removePass(this.fxaaPass);
    }

    if (this.el.sceneEl.renderer) {
      this.el.sceneEl.renderer.render = this.originalRender;
    }
    if (this.composer) {
      this.composer.dispose();
    }

    if (this.smaaPass) {
      this.composer.removePass(this.smaaPass);
    }

    if (this.renderStartListener) {
      this.el.sceneEl.removeEventListener('renderstart', this.renderStartListener);
    }
  }
});





export const CameraComponent = {
  cinematicCamera: AFRAME.components['cinematic-camera'],
  cinematicCameraAdv: AFRAME.components['cinematic-camera-adv'],
  cinematicCameraAdv2: AFRAME.components['cinematic-camera-adv2'],
  cinematicCameraAdv3: AFRAME.components['cinematic-camera-adv3'],
  cinematicCameraAdv4: AFRAME.components['cinematic-camera-adv4'],
  ssao: AFRAME.components['ssao']
}